import { AiPipeline } from '../../core/models/ai.model';
import {
  AiActionPolicy,
  AiChatMessage,
  AiResultAction,
  AiResultActionState,
  AiResultBinding,
  AiResultContext,
  AiResultField,
  AiResultFieldView,
  AiResultBindingPreview,
  AiRunnerConfig,
  AiRunnerData,
  AiRunnerFile,
  AiRunnerInput,
  AiRunnerOption,
  AiRunnerOptionValue,
  AiRunnerPipelineRef,
  AiRunnerOptionValues,
  AiRunnerVariant,
} from './ai-runner.model';

export const AI_RUNNER_DEFAULT_MAX_MB = 5;

/** Substitui `{{prompt}}`, `{{data.caminho}}` e `{{options.chave}}`; sem valor vira vazio. */
export function renderPromptTemplate(
  template: string,
  prompt: string,
  data: AiRunnerData | undefined,
  options: AiRunnerOptionValues = {}
): string {
  const show = (value: unknown) => {
    if (value === undefined || value === null) return '';
    return typeof value === 'object' ? JSON.stringify(value) : String(value);
  };
  return template.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_match, key: string) => {
    if (key === 'prompt') return prompt;
    if (key.startsWith('data.')) return show(readPath(data, key.slice(5)));
    if (key.startsWith('options.')) return show(readPath(options, key.slice(8)));
    return '';
  });
}

/** Monta o `input` da execução. Campos vazios são omitidos. */
export function buildRunInput(
  config: AiRunnerConfig,
  prompt: string,
  data: AiRunnerData | undefined,
  files: AiRunnerFile[],
  history: AiChatMessage[] = [],
  variant: AiRunnerVariant | null = null,
  optionValues: AiRunnerOptionValues = {}
): AiRunnerInput {
  const typed = prompt.trim();
  const template = variant?.promptTemplate ?? config.promptTemplate;
  const composed = template
    ? renderPromptTemplate(template, typed, data, optionValues).trim()
    : typed;

  const input: AiRunnerInput = {};
  if (composed) input.prompt = composed;
  if (data && Object.keys(data).length) input.data = data;
  if (files.length) input.files = files;
  if (config.mode === 'chat' && history.length) {
    input.messages = history
      .filter(message => !message.error)
      .map(({ role, content }) => ({ role, content }));
  }
  if (config.refType) input.ref_type = config.refType;
  if (config.refId) input.ref_id = config.refId;
  if (variant) input.variant = variant.pipeline ?? variant.key;
  const sent = visibleOptions(config.options ?? [], optionValues, variant?.key ?? null).filter(
    option => option.send !== false
  );
  if (sent.length) {
    input.options = Object.fromEntries(
      sent.map(option => [option.key, optionValues[option.key] ?? null])
    );
  }
  return input;
}

/** Erro de validação dos arquivos escolhidos, ou `null`. */
export function validateFiles(
  files: readonly { name: string; size: number }[],
  maxFiles: number | undefined,
  maxSizeMb = AI_RUNNER_DEFAULT_MAX_MB
): string | null {
  if (maxFiles && files.length > maxFiles) {
    return `No máximo ${maxFiles} arquivo(s).`;
  }
  const big = files.find(file => file.size > maxSizeMb * 1024 * 1024);
  return big ? `"${big.name}" passa de ${maxSizeMb} MB.` : null;
}

export function fileToRunnerFile(file: File): Promise<AiRunnerFile> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => {
      const result = String(reader.result ?? '');
      resolve({
        name: file.name,
        mime: file.type || 'application/octet-stream',
        size: file.size,
        base64: result.slice(result.indexOf(',') + 1),
      });
    };
    reader.readAsDataURL(file);
  });
}

/** Lê `a.b.0.c`; `undefined` quando o caminho não existe. */
export function readPath(source: unknown, path: string): unknown {
  if (!path) return source;
  return path.split('.').reduce<unknown>((current, key) => {
    if (current === null || current === undefined || typeof current !== 'object') return undefined;
    return (current as Record<string, unknown>)[key];
  }, source);
}

/**
 * JSON do resultado: `output_data` quando é objeto; senão o `output` em texto, aceitando bloco
 * ```json``` ou o primeiro `{...}`/`[...]` do texto.
 */
export function extractResultJson(outputData: unknown, output: string | null | undefined): unknown {
  if (outputData && typeof outputData === 'object') return outputData;
  const candidates = [outputData, output].filter(
    (value): value is string => typeof value === 'string' && !!value.trim()
  );
  for (const text of candidates) {
    const fenced = text.match(/```(?:json)?\s*([\s\S]*?)```/i)?.[1];
    const braces = text.match(/[[{][\s\S]*[\]}]/)?.[0];
    for (const raw of [fenced, text, braces]) {
      if (!raw) continue;
      try {
        return JSON.parse(raw.trim());
      } catch {
        // tenta o próximo formato
      }
    }
  }
  return null;
}

export function previewBindings(
  bindings: readonly AiResultBinding[],
  json: unknown
): AiResultBindingPreview[] {
  return bindings.map(binding => {
    const raw = readPath(json, binding.path);
    const found = raw !== undefined;
    const value = found && binding.transform ? binding.transform(raw) : raw;
    const display = !found
      ? 'não encontrado no resultado'
      : binding.format
        ? binding.format(value)
        : typeof value === 'object'
          ? JSON.stringify(value)
          : String(value);
    return { binding, value, found, display };
  });
}

/** Texto do resultado para exibir/usar como resposta do chat. */
export function resultText(output: string | null | undefined, outputData: unknown): string {
  if (output && output.trim()) return output;
  if (outputData === null || outputData === undefined) return '';
  return typeof outputData === 'string' ? outputData : JSON.stringify(outputData, null, 2);
}

/** Variante escolhida dentro da config (ou de uma lista já montada), ou `null`. */
export function findVariant(
  source: AiRunnerConfig | readonly AiRunnerVariant[],
  key: string | null | undefined
): AiRunnerVariant | null {
  const list = Array.isArray(source)
    ? (source as readonly AiRunnerVariant[])
    : ((source as AiRunnerConfig).variants ?? []);
  return (key && list.find(variant => variant.key === key || variant.pipeline === key)) || null;
}

/** Normaliza a referência de pipeline do modo lista. */
export function pipelineRef(ref: AiRunnerPipelineRef): { id?: string; slug?: string } {
  return typeof ref === 'string' ? { id: ref } : ref;
}

/**
 * Modo lista: um cartão por pipeline carregada, na ordem de `refs`, complementado pela variante
 * cujo `pipeline` é o id ou o slug dela. Referências que não carregaram ficam de fora.
 */
export function buildPipelineVariants(
  refs: readonly AiRunnerPipelineRef[],
  loaded: readonly Pick<AiPipeline, 'id' | 'slug' | 'name' | 'description'>[],
  variants: readonly AiRunnerVariant[] = []
): AiRunnerVariant[] {
  const result: AiRunnerVariant[] = [];
  for (const raw of refs) {
    const ref = pipelineRef(raw);
    const same = (x?: string | null, y?: string | null) =>
      !!x && !!y && x.toLowerCase() === y.toLowerCase();
    const pipeline = loaded.find(p => (ref.id ? same(p.id, ref.id) : same(p.slug, ref.slug)));
    if (!pipeline || result.some(item => item.key === pipeline.id)) continue;
    const extra = variants.find(
      v => same(v.pipeline, pipeline.id) || same(v.pipeline, pipeline.slug)
    );
    result.push({
      ...extra,
      key: pipeline.id,
      label: extra?.label ?? pipeline.name,
      description: extra?.description ?? pipeline.description ?? undefined,
      pipeline: pipeline.slug,
      pipelineId: pipeline.id,
    });
  }
  return result;
}

/** Estado inicial das ações, conforme a política, para um resultado concluído. */
export function resolveActionStates(
  actions: readonly AiResultAction[],
  policy: AiActionPolicy | undefined,
  ctx: AiResultContext
): AiResultActionState[] {
  const usable = actions.filter(action => {
    try {
      return action.available ? action.available(ctx) : true;
    } catch {
      return false;
    }
  });
  const fixed = policy?.mode === 'fixed' ? policy.keys : null;
  const preselect = policy?.mode === 'choose' ? (policy.preselect ?? []) : [];
  return usable
    .filter(action => !fixed || fixed.includes(action.key))
    .map(action => {
      let preview: string | null = null;
      try {
        preview = action.preview ? action.preview(ctx) : null;
      } catch {
        preview = null;
      }
      return {
        action,
        selected: !!fixed || preselect.includes(action.key),
        locked: !!fixed,
        status: 'idle' as const,
        preview,
        error: null,
      };
    });
}

function stringifyValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  return typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value);
}

/** Visão de cada campo de `resultView.fields` a partir do JSON. */
export function buildFieldViews(
  fields: readonly AiResultField[],
  json: unknown
): AiResultFieldView[] {
  return fields
    .map(field => {
      const value = readPath(json, field.path);
      const found = value !== undefined && value !== null && value !== '';
      const items = Array.isArray(value) ? value.map(item => stringifyValue(item)) : [];
      const max = field.max ?? 10;
      const numeric = Number(value);
      const percent = Number.isFinite(numeric)
        ? Math.min(100, Math.max(0, Math.round((numeric / max) * 100)))
        : 0;
      let text = '';
      try {
        text = field.format ? field.format(value) : stringifyValue(value);
      } catch {
        text = stringifyValue(value);
      }
      return {
        field,
        value,
        found,
        text,
        items,
        percent,
        severity: found && field.severity ? field.severity(value) : null,
      };
    })
    .filter(view => view.found || view.field.hideWhenMissing === false);
}

function defaultValue(option: AiRunnerOption): AiRunnerOptionValue {
  switch (option.type) {
    case 'checkbox':
      return option.default ?? false;
    case 'number':
      return option.default ?? null;
    case 'select':
      return option.default ?? (option.multiple ? [] : null);
    default:
      return option.default ?? (option.type === 'record' ? null : '');
  }
}

/** O valor salvo só vale se tiver o tipo que o campo espera (o cadastro pode ter mudado). */
function matchesType(option: AiRunnerOption, value: unknown): value is AiRunnerOptionValue {
  if (value === null) return option.type !== 'checkbox';
  switch (option.type) {
    case 'checkbox':
      return typeof value === 'boolean';
    case 'number':
      return typeof value === 'number' && Number.isFinite(value);
    case 'select':
      return option.multiple
        ? Array.isArray(value) &&
            value.every(item => option.choices.some(choice => choice.value === item))
        : typeof value === 'string' && option.choices.some(choice => choice.value === value);
    default:
      return typeof value === 'string';
  }
}

/** Valores iniciais: o padrão de cada campo ou, com `remember`, o último valor salvo. */
export function initialOptionValues(
  options: readonly AiRunnerOption[],
  stored: Record<string, unknown> = {}
): AiRunnerOptionValues {
  const values: AiRunnerOptionValues = {};
  for (const option of options) {
    const saved = stored[option.key];
    values[option.key] =
      option.remember && saved !== undefined && matchesType(option, saved)
        ? saved
        : defaultValue(option);
  }
  return values;
}

/** Campos visíveis para a variante e os valores atuais. */
export function visibleOptions(
  options: readonly AiRunnerOption[],
  values: AiRunnerOptionValues,
  variantKey: string | null
): AiRunnerOption[] {
  return options.filter(option => {
    if (option.variants?.length && (!variantKey || !option.variants.includes(variantKey))) {
      return false;
    }
    try {
      return option.visible ? option.visible(values) : true;
    } catch {
      return true;
    }
  });
}

function isEmpty(value: AiRunnerOptionValue | undefined): boolean {
  if (value === null || value === undefined || value === false) return true;
  if (typeof value === 'string') return !value.trim();
  if (Array.isArray(value)) return !value.length;
  return false;
}

/** Mensagem do primeiro obrigatório vazio, ou `null`. */
export function validateOptions(
  visible: readonly AiRunnerOption[],
  values: AiRunnerOptionValues
): string | null {
  const missing = visible.find(option => option.required && isEmpty(values[option.key]));
  return missing ? `Preencha "${missing.label}".` : null;
}
